import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, GripVertical, Loader2, Plus, Save } from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FEATURE_ICONS } from "@/components/FeatureCardsGrid";
import { FeatureCardEditor } from "@/components/admin/website/FeatureCardEditor";
import { PageEditor } from "@/components/admin/website/PageEditor";

const StatusBadge = ({ c }) => {
  const label = !c.enabled ? "disabled" : c.status === "published" ? (c.has_unpublished_changes ? "published · draft changes" : "published") : "draft";
  const tone = !c.enabled ? "bg-slate-200 text-slate-600" : c.status === "published" ? (c.has_unpublished_changes ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700") : "bg-amber-100 text-amber-700";
  return <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${tone}`} data-testid={`fc-status-${c.card_id}`}>{label}</span>;
};

export const FeatureCardsManager = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [sectionDraft, setSectionDraft] = useState({ heading: "", subheading: "" });
  const [editing, setEditing] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/feature-cards`, { headers });
    setData(r.data);
    setSectionDraft((cur) => (cur.touched ? cur : { heading: r.data.heading, subheading: r.data.subheading }));
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const saveOrder = async (cards) => {
    setData((d) => ({ ...d, cards }));
    await axios.put(`${API}/admin/feature-cards/reorder`, { order: cards.map((c) => c.card_id) }, { headers });
    toast.success("Order saved");
  };
  const move = (i, dir) => {
    const cards = [...data.cards]; const j = i + dir;
    if (j < 0 || j >= cards.length) return;
    [cards[i], cards[j]] = [cards[j], cards[i]]; saveOrder(cards);
  };
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const cards = [...data.cards];
    const from = cards.findIndex((c) => c.card_id === dragId), to = cards.findIndex((c) => c.card_id === targetId);
    cards.splice(to, 0, cards.splice(from, 1)[0]); setDragId(null); saveOrder(cards);
  };
  const saveSection = async () => {
    try { await axios.put(`${API}/admin/feature-cards/section`, { heading: sectionDraft.heading, subheading: sectionDraft.subheading }, { headers }); toast.success("Section heading saved - live now"); setSectionDraft((c) => ({ ...c, touched: false })); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };
  const create = async () => {
    const title = window.prompt("Name of the new feature card (also used for its page address)", "");
    if (!title || !title.trim()) return;
    try { const r = await axios.post(`${API}/admin/feature-cards`, { title: title.trim() }, { headers }); await load(); setEditing(r.data.card_id); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't create card"); }
  };

  if (editingPage) return <PageEditor pageId={editingPage} onBack={() => { setEditingPage(null); load(); }} />;
  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const current = data.cards.find((c) => c.card_id === editing);
  if (current) return <FeatureCardEditor card={current} icons={data.icons} headers={headers} onBack={() => { setEditing(null); load(); }} onChanged={load} onEditPage={setEditingPage} />;

  return (
    <div className="space-y-5" data-testid="feature-cards-manager">
      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-sm font-semibold">"Everything You Need" section</p>
        <div className="grid md:grid-cols-2 gap-3">
          <Input value={sectionDraft.heading} onChange={(e) => setSectionDraft({ ...sectionDraft, heading: e.target.value, touched: true })} placeholder="Heading" data-testid="fc-section-heading" />
          <Input value={sectionDraft.subheading} onChange={(e) => setSectionDraft({ ...sectionDraft, subheading: e.target.value, touched: true })} placeholder="Sub-heading" data-testid="fc-section-subheading" />
        </div>
        <Button size="sm" onClick={saveSection} data-testid="fc-section-save"><Save className="w-4 h-4 mr-1" />Save heading</Button>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Drag cards (or use the arrows) to change the order shown on the website. Click a card to edit it and its content page.</p>
        <Button size="sm" onClick={create} data-testid="fc-create"><Plus className="w-4 h-4 mr-1" />Add card</Button>
      </div>
      <div className="space-y-2">
        {data.cards.map((c, i) => {
          const Icon = FEATURE_ICONS[c.draft.icon] || FEATURE_ICONS.Sparkles;
          return (
            <div key={c.card_id} draggable onDragStart={() => setDragId(c.card_id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(c.card_id)}
              className={`flex items-center gap-3 rounded-xl border p-3 bg-white transition-[box-shadow,opacity] hover:shadow-sm ${dragId === c.card_id ? "opacity-50" : ""}`} data-testid={`fc-row-${c.card_id}`}>
              <GripVertical className="w-4 h-4 text-slate-400 cursor-grab flex-shrink-0" />
              <span className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-primary" /></span>
              <button className="flex-1 min-w-0 text-left" onClick={() => setEditing(c.card_id)} data-testid={`fc-open-${c.card_id}`}>
                <p className="font-semibold truncate">{c.draft.title}</p>
                <p className="text-xs text-muted-foreground truncate">{c.draft.link_type === "url" ? c.draft.url : c.page ? `/${c.page.slug}` : "no page"}</p>
              </button>
              <StatusBadge c={c} />
              <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`fc-up-${c.card_id}`}><ArrowUp className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === data.cards.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`fc-down-${c.card_id}`}><ArrowDown className="w-4 h-4" /></Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
